import { useState } from "react";
import { auth } from "../firebaseConfig";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useNavigate, Link } from "react-router-dom";
import { api, endpoints } from "../apiConfig";
import AuthLayout from "../components/AuthLayout";
import toast from 'react-hot-toast';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const navigate = useNavigate();

  const handleSync = async () => {
    if (!email) {
      toast.error('Please enter your email first');
      return;
    }

    try {
      setSyncLoading(true);
      setError('');
      
      const response = await api.post(endpoints.user.devSync, { email });
      console.log('Sync response:', response.data);
      
      toast.success('Account synced successfully! Please sign in.');
    } catch (error) {
      console.error('Sync error:', error);
      toast.error(error.response?.data?.message || 'Failed to sync account');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // First sign in with Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const token = await userCredential.user.getIdToken();
      
      // Set token in localStorage and API headers
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      try {
        // Try to get existing profile first
        const profileResponse = await api.get(endpoints.user.profile);
        localStorage.setItem('user', JSON.stringify(profileResponse.data));
        navigate("/dashboard");
        return;
      } catch (profileError) {
        if (profileError.response?.status !== 404) {
          throw profileError;
        }
        
        // Initialize new user without forcing a role
        const initResponse = await api.post(endpoints.user.initializeIfNeeded, {});
        localStorage.setItem('user', JSON.stringify(initResponse.data));
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Login error:", error);
      
      // Axios network-level error (no response)
      if (!error.response && (error.message === 'Network Error' || error.code === 'ERR_NETWORK')) {
        setError('Unable to reach Art Emerge servers. Please check your connection and try again.');
      } else if (error.response?.status === 404) {
        setError('Account not found. Please try using the sync button below.');
      } else if (error.response?.status === 401) {
        setError('Invalid email or password');
      } else if (error.code === 'auth/user-not-found') {
        setError('No account found with this email. Please sign up first.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address format.');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error contacting Google (Firebase). Please check your connection and try again.');
      } else {
        setError(error.message || 'Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Welcome back">
      <form onSubmit={handleSubmit} className="login-form">
        <div className="form-group">
          <input
            type="email"
            id="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="login-input"
          />
        </div>
        <div className="form-group">
          <input
            type="password"
            id="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="login-input"
          />
        </div>
        {error && <div className="login-message error">{error}</div>}
        <button type="submit" className="login-button" disabled={loading}>
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
        <div className="login-links">
          <Link to="/forgot-password" className="login-link">Forgot Password?</Link>
        </div>
        {process.env.NODE_ENV === 'development' && (
          <div className="sync-section">
            <p className="login-message">Having trouble signing in?</p>
            <button 
              className="login-button secondary"
              onClick={handleSync}
              disabled={syncLoading || !email}
            >
              {syncLoading ? 'Syncing...' : 'Sync Account'}
            </button>
          </div>
        )}
      </form>
    </AuthLayout>
  );
};

export default Login;